import { PublicClientApplication, LogLevel } from '@azure/msal-browser'

// Validate required environment variables for authentication
const requiredEnvVars = [
  'VUE_APP_CLIENT_ID',
  'VUE_APP_API_AUTHORITY',
  'VUE_APP_API_SCOPES',
  'VUE_APP_GRAPH_SCOPES'
];
requiredEnvVars.forEach((varName) => {
  if (!process.env[varName]) {
    throw new Error(`Missing required environment variable: ${varName}`);
  }
});

// Parse scopes from environment variables
const parseScopes = (scopeString) => {
  return scopeString ? scopeString.split(',').map(scope => scope.trim()) : []
}

const apiScopes = parseScopes(process.env.VUE_APP_API_SCOPES)
const graphScopes = parseScopes(process.env.VUE_APP_GRAPH_SCOPES)

// MSAL Configuration
const msalConfig = {
  auth: {
    clientId: process.env.VUE_APP_CLIENT_ID, // Replace with your Entra ID app registration client ID
    authority: process.env.VUE_APP_API_AUTHORITY, // Replace with your tenant ID
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'localStorage', // Use localStorage to persist across tabs
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return
        switch (level) {
          case LogLevel.Error:
            console.error(message)
            return
          case LogLevel.Info:
            console.info(message)
            return
          case LogLevel.Verbose:
            console.debug(message)
            return
          case LogLevel.Warning:
            console.warn(message)
            return
        }
      },
    },
    // Prevent nested popups
    allowNativeBroker: false,
    // Handle redirect timeouts
    redirectNavigationTimeout: 5000,
  },
}

// Request configuration for login - include both API and Graph scopes
const loginRequest = {
  scopes: [...apiScopes, ...graphScopes], // Include all scopes for login
  prompt: 'select_account',
}

// Request configurations for different token types
const apiTokenRequest = {
  scopes: apiScopes,
  account: null,
}

const graphTokenRequest = {
  scopes: graphScopes,
  account: null,
}

class AuthService {
  constructor() {
    // Check if running in Cypress
    this.isCypress = window.Cypress !== undefined

    if (!this.isCypress) {
      this.msalInstance = new PublicClientApplication(msalConfig)
    }
    this.account = null
  }

  async initialize() {
    // Cypress mock mode
    if (this.isCypress && window.__CYPRESS_MOCK_AUTH__) {
      console.log('AuthService: Using Cypress mock authentication')
      this.account = window.__CYPRESS_MOCK_AUTH__.account
      return this.account
    }

    try {
      await this.msalInstance.initialize()

      // Handle the redirect promise after a login or token redirect
      const response = await this.msalInstance.handleRedirectPromise()

      if (response) {
        console.log('AuthService: Handle redirect promise success')
        this.account = response.account
      } else {
        // No redirect response, check if we have cached accounts
        const accounts = this.msalInstance.getAllAccounts()
        console.log('AuthService: Found', accounts.length, 'existing accounts')
        if (accounts.length > 0) {
          this.account = accounts[0]
        }
      }

      // Return the account so the store can use it
      return this.account
    } catch (error) {
      console.error('MSAL initialization failed:', error)
      throw error
    }
  }

  async login() {
    try {
      // Use redirect instead of popup
      await this.msalInstance.loginRedirect(loginRequest)
      // In Cypress, we need to return to let tests continue
      if (this.isCypress) {
        return
      }

      // Return a promise that never resolves to halt execution while redirecting
      // This prevents downstream code (like authStore state updates) from running
      // while the page is unloading/redirecting, effectively acting as a "pause"
      return new Promise(() => { })
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }

  async logout() {
    try {
      if (this.msalInstance) {
        await this.msalInstance.clearCache({
          account: this.account,
        })
      }
      this.account = null
      return true
    } catch (error) {
      console.error('Logout failed:', error)
      throw error
    }
  }

  async getAccessToken(scopeType = 'api') {
    // Cypress mock mode
    if (this.isCypress && window.__CYPRESS_MOCK_AUTH__) {
      return {
        accessToken: window.__CYPRESS_MOCK_AUTH__.accessToken,
        correlationId: 'mock-correlation-id'
      }
    }

    if (!this.account) {
      throw new Error('No account available')
    }

    // Choose the appropriate token request based on scope type
    const tokenRequest = scopeType === 'graph' ? { ...graphTokenRequest } : { ...apiTokenRequest }
    tokenRequest.account = this.account

    try {
      // Try to get token silently first
      const response = await this.msalInstance.acquireTokenSilent(tokenRequest)
      return { accessToken: response.accessToken, correlationId: response.correlationId }
    } catch (error) {
      console.warn('Silent token acquisition failed, trying redirect:', error)

      // If silent acquisition fails, use redirect
      try {
        console.log('AuthService: Silent token failed, acquiring via redirect')
        await this.msalInstance.acquireTokenRedirect(tokenRequest)
        // Return a promise that never resolves to halt execution while redirecting
        return new Promise(() => { })
      } catch (redirectError) {
        console.error('Token acquisition redirect failed:', redirectError)
        throw redirectError
      }
    }
  }

  // Get token specifically for API calls
  async getApiToken() {
    return this.getAccessToken('api')
  }

  // Get token specifically for Graph API calls
  async getGraphToken() {
    return this.getAccessToken('graph')
  }

  async isAuthenticated() {
    return !!this.account
  }

  getAccount() {
    return this.account
  }

  getUserInfo() {
    if (!this.account) return null

    return {
      id: this.account?.homeAccountId,
      username: this.account?.idTokenClaims?.preferred_username,
      name: this.account?.idTokenClaims?.name,
      email: this.account?.idTokenClaims?.email,
    }
  }

  // Decode base64url payload of a JWT
  decodeJwtPayload(token) {
    try {
      // Validate JWT structure before attempting to decode
      if (typeof token !== 'string') return null
      const parts = token.split('.')
      if (parts.length !== 3) return null
      const payload = parts[1]
      if (!payload) return null
      // Ensure payload uses valid base64url charset
      if (!/^[A-Za-z0-9_-]+={0,2}$/.test(payload)) return null
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
      const json = decodeURIComponent(
        atob(padded)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
          })
          .join('')
      )
      return JSON.parse(json)
    } catch (e) {
      console.error('Failed to decode JWT payload', e)
      return null
    }
  }

  // Get application roles from the ACCESS token (not ID token)
  async getRoles(scopeType = 'api') {
    const { accessToken } = await this.getAccessToken(scopeType)
    const payload = this.decodeJwtPayload(accessToken)
    if (!payload) return []
    // Azure AD app roles typically in 'roles' (array); fallback to 'role'
    const roles = payload.roles || payload.role || []
    return Array.isArray(roles) ? roles : [roles].filter(Boolean)
  }
}

export default new AuthService()
